"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useDefaultDetailsMiddleware = exports.parseMoment = void 0;
const const_1 = require("@app/const");
const graphql_1 = require("@app/gql-types/graphql");
const request_1 = require("@app/request");
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const maybe_unwrap_1 = require("@app/util/apollo/maybe-unwrap");
const error_handler_1 = require("@app/util/error-handler");
const maybe_1 = require("@app/util/functional/maybe");
const use_workspace_unavailability_categories_1 = require("@app/util/hooks/use-workspace-unavailability-categories");
const useMe_1 = require("@app/util/hooks/useMe");
const useWorkspace_1 = require("@app/util/hooks/useWorkspace");
const use_calendar_settings_1 = require("@app/util/use-calendar-settings");
const user_option_parser_1 = require("@app/util/user-option-parser");
const moment_1 = __importDefault(require("moment"));
const use_find_active_contract_1 = require("../use-find-active-contract");
const isWholeDay = (start, end) => {
    return ((0, moment_1.default)(start).format('HH:mm') === '00:00' &&
        ((0, moment_1.default)(end).format('HH:mm') === '00:00' ||
            (0, moment_1.default)(end).format('HH:mm') === '23:59'));
};
const parseMoment = (value) => (0, moment_1.default)(value, [moment_1.default.ISO_8601, 'YYYY-MM-DD'], true);
exports.parseMoment = parseMoment;
const useLoadEmptyTimeOffData = () => {
    const workspace = (0, useWorkspace_1.useWorkspace)();
    const findActiveContract = (0, use_find_active_contract_1.useFindActiveContract)();
    const userOptions = (0, store_1.useRootSelector)((state) => state.me.options?.filter(util_1.isValue) ?? []);
    const userOptionsParser = new user_option_parser_1.UserOptionParser(userOptions);
    const timeOffDefaultInclusion = userOptionsParser.getEnum('timeOffDefaultInclusion', graphql_1.TimeOffWorkMinutesInclusion) ?? graphql_1.TimeOffWorkMinutesInclusion.EveryDay;
    const timeOffDefaultWorkMinutesBy = userOptionsParser.getEnum('timeOffWorkMinutesBy', graphql_1.TimeOffWorkMinutesBy) ??
        graphql_1.TimeOffWorkMinutesBy.Shift;
    const timeOffDefaultWorkMinutes = userOptionsParser.getNumber('timeOffCustomInclusion');
    const categories = (0, use_workspace_unavailability_categories_1.getWorkspaceUnavailabilityCategories)(workspace);
    const vacationCategory = categories
        .filter((cat) => !cat.archived)
        .find((cat) => cat.vacation === true);
    return async (initial, config) => {
        const periodEndFallback = new maybe_1.Maybe(initial?.period?.start)
            .map(exports.parseMoment)
            .map((_start) => _start.endOf('day'))
            .get(() => undefined);
        const periodStart = new maybe_1.Maybe(initial?.period?.start)
            .map(exports.parseMoment)
            .map((_start) => _start.startOf('day'))
            .get(() => undefined);
        const periodEnd = new maybe_1.Maybe(initial?.period?.end)
            .map(exports.parseMoment)
            .map((_end) => _end.endOf('day'))
            .get(() => periodEndFallback);
        const tempDefaultDetails = {
            user: (0, maybe_unwrap_1.unwrapMaybe)(config.userId),
            period: {
                start: periodStart,
                end: periodEnd,
            },
            note: initial?.note,
            periodType: initial?.periodType ?? graphql_1.PeriodType.WholeDay,
            contractId: findActiveContract(config.userId, periodStart)?.id,
            categoryId: initial?.categoryId ?? vacationCategory?.id,
            workMinutesInclusion: timeOffDefaultInclusion,
            workMinutesBy: timeOffDefaultWorkMinutesBy,
            workMinutes: timeOffDefaultWorkMinutes,
            requestPendingAction: initial?.requestPendingAction,
        };
        if (initial?.requestPendingAction) {
            const action = await (0, request_1.replyActionId)({
                actionId: initial.requestPendingAction,
            });
            const _period = action?.data?.period || initial.period;
            tempDefaultDetails.period = {
                start: (0, moment_1.default)(_period.start),
                end: (0, moment_1.default)(_period.end),
            };
        }
        if (tempDefaultDetails.periodType === graphql_1.PeriodType.PartDay) {
            config.setTimePeriodInput({
                start: (0, moment_1.default)(tempDefaultDetails.period?.start),
                end: (0, moment_1.default)(tempDefaultDetails.period?.end),
            });
        }
        return tempDefaultDetails;
    };
};
const useLoadTimeOffData = () => {
    const { canRead } = (0, util_1.useAccessRole)();
    const workspace = (0, useWorkspace_1.useWorkspace)();
    const me = (0, useMe_1.useMe)();
    const calendarSettings = (0, use_calendar_settings_1.useCalendarSettings)();
    return async (initial, config) => {
        // this naturally won't happen, but have to make typescript happy
        if ((0, util_1.isUndefined)(config.timeOffId))
            return {};
        const attachmentsRes = await (0, request_1.requestTimeOffAttachments)({
            timeOffId: config.timeOffId,
            workspaceId: canRead(const_1.PERMISSIONS.CALENDAR) ||
                canRead(const_1.PERMISSIONS.BACKOFFICE.ATTENDANCE) ||
                canRead(const_1.PERMISSIONS.BACKOFFICE.TIMEOFFREQUESTS)
                ? workspace?.id
                : undefined,
            userId: !canRead(const_1.PERMISSIONS.CALENDAR) &&
                !canRead(const_1.PERMISSIONS.BACKOFFICE.ATTENDANCE) &&
                !canRead(const_1.PERMISSIONS.BACKOFFICE.TIMEOFFREQUESTS)
                ? me.id
                : undefined,
        });
        const attachments = (0, error_handler_1.isError)(attachmentsRes) ? undefined : attachmentsRes;
        const _periodStart = (0, moment_1.default)(initial?.period?.start);
        let _periodEnd = (0, moment_1.default)(initial?.period?.end);
        const timeOffDurationDays = _periodEnd.diff(_periodStart, 'days');
        if (_periodStart.get('date') !== _periodEnd.get('date') &&
            timeOffDurationDays === 1) {
            _periodEnd = (0, moment_1.default)(_periodStart).endOf('day');
        }
        const timeOffDetails = {
            ...initial,
            id: config.timeOffId,
            user: initial?.userId,
            period: {
                start: _periodStart,
                end: _periodEnd,
            },
            note: initial?.note,
            attachments: attachments?.map((att) => att.file).filter(util_1.isValue),
            periodType: (0, util_1.isValue)(_periodStart) && (0, util_1.isValue)(_periodEnd)
                ? isWholeDay(_periodStart.toDate(), _periodEnd.toDate())
                    ? graphql_1.PeriodType.WholeDay
                    : graphql_1.PeriodType.PartDay
                : initial?.periodType,
            workMinutesInclusion: calendarSettings.timeOffDefaultInclusion,
        };
        if (initial?.requestPendingAction) {
            const action = await (0, request_1.replyActionId)({
                actionId: initial.requestPendingAction,
            });
            const _period = action?.data?.period || initial.period;
            timeOffDetails.period = {
                start: (0, moment_1.default)(_period.start),
                end: (0, moment_1.default)(_period.end),
            };
        }
        if (timeOffDetails.periodType === graphql_1.PeriodType.PartDay) {
            config.setTimePeriodInput({
                start: _periodStart,
                end: _periodEnd,
            });
        }
        return timeOffDetails;
    };
};
const useLoadAvailabilityData = () => {
    return async (initial, config) => {
        if ((0, util_1.isUndefined)(config.availabilityId))
            return {};
        const start = (0, moment_1.default)(initial?.period?.start);
        const end = (0, moment_1.default)(initial?.period?.end);
        const isWholeDay = end.diff(start, 'hours', true) === 24;
        const periodType = isWholeDay ? graphql_1.PeriodType.WholeDay : graphql_1.PeriodType.PartDay;
        if (periodType === graphql_1.PeriodType.PartDay) {
            config.setTimePeriodInput({
                start,
                end,
            });
        }
        return {
            ...initial,
            id: config.availabilityId,
            user: initial?.userId,
            categoryId: initial?.available ? use_workspace_unavailability_categories_1.AVAILABILITY_VALUE : use_workspace_unavailability_categories_1.UNAVAILABILITY_VALUE,
            period: {
                start,
                end,
            },
            periodType,
        };
    };
};
const useDefaultDetailsMiddleware = (initialData, setTimePeriodInput) => {
    const workspace = (0, useWorkspace_1.useWorkspace)();
    const availabilities = (0, store_1.useRootSelector)((state) => state.availabilities);
    const timeOffs = (0, store_1.useRootSelector)((state) => state.timeOffs);
    const targetId = (0, maybe_unwrap_1.unwrapMaybe)(initialData?.id);
    const userId = (0, maybe_unwrap_1.unwrapMaybe)(initialData?.userId);
    const loadEmptyTimeOffData = useLoadEmptyTimeOffData();
    const loadTimeOffData = useLoadTimeOffData();
    const loadAvailabilityData = useLoadAvailabilityData();
    return async () => {
        if (!(0, util_1.isValue)(workspace))
            return await loadEmptyTimeOffData(initialData, { userId, setTimePeriodInput });
        if (!(0, util_1.isValue)(targetId) && !(0, util_1.isValue)(userId))
            return await loadEmptyTimeOffData(initialData, { userId, setTimePeriodInput });
        // unav ID was passed - editing existing unav
        const availabilityObj = availabilities.find((evt) => evt.id === targetId);
        const timeOffObj = timeOffs.find((evt) => evt.id === targetId);
        if ((0, util_1.isDefined)(availabilityObj)) {
            return await loadAvailabilityData(availabilityObj, {
                availabilityId: targetId,
                setTimePeriodInput,
            });
        }
        if ((0, util_1.isDefined)(timeOffObj)) {
            return await loadTimeOffData(timeOffObj, {
                timeOffId: targetId,
                setTimePeriodInput,
            });
        }
        // no ID was passed - loading default stuff
        return await loadEmptyTimeOffData(initialData, { userId, setTimePeriodInput });
    };
};
exports.useDefaultDetailsMiddleware = useDefaultDetailsMiddleware;
